import { inject, Injectable, OnDestroy, signal, computed } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, interval, Subscription, switchMap, startWith, catchError, of } from 'rxjs';
import { EnvironmentService } from '../environment/environment.service';

export interface ApiNotification {
  id: number;
  user_id: number;
  title: string;
  message: string | null;
  category: string;
  event: string;
  link: string | null;
  priority: 'high' | 'medium' | 'low';
  channel: string;
  resource_type: string | null;
  resource_id: number | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

export interface NotificationListResponse {
  items: ApiNotification[];
  total: number;
  unread_count: number;
  page: number;
  page_size: number;
}

export interface UnreadCountResponse {
  count: number;
}

export interface BulkActionResponse {
  affected: number;
  message: string;
}

export interface NotificationFilters {
  is_read?: boolean;
  category?: string;
  priority?: string;
  page?: number;
  page_size?: number;
}

const POLL_INTERVAL_MS = 60_000;

@Injectable({
  providedIn: 'root',
})
export class ApiNotificationService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(EnvironmentService);

  private readonly API_URL = this.environment.getApiUrl() + 'v1/notifications';

  private _unreadCount = signal<number>(0);
  private _inboxNotifications = signal<ApiNotification[]>([]);
  private _inboxLoading = signal<boolean>(true);
  private _pollingSubscription: Subscription | null = null;

  readonly unreadCount = this._unreadCount.asReadonly();
  readonly inboxNotifications = this._inboxNotifications.asReadonly();
  readonly isInboxLoading = this._inboxLoading.asReadonly();
  readonly hasUnread = computed(() => this._unreadCount() > 0);
  readonly unreadBadge = computed(() => {
    const count = this._unreadCount();
    return count > 0 ? (count > 99 ? '99+' : count.toString()) : null;
  });

  startPolling(): void {
    if (this._pollingSubscription) return;

    this._pollingSubscription = interval(POLL_INTERVAL_MS)
      .pipe(
        startWith(0),
        switchMap(() => this.getUnreadCount().pipe(catchError(() => of({ count: 0 })))),
      )
      .subscribe((response) => {
        this._unreadCount.set(response.count);
      });

    this.loadInbox();
  }

  stopPolling(): void {
    this._pollingSubscription?.unsubscribe();
    this._pollingSubscription = null;
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  loadInbox(page_size = 10): void {
    this._inboxLoading.set(true);
    this.list({ page: 1, page_size }).subscribe({
      next: (response) => {
        this._inboxNotifications.set(response.items);
        this._unreadCount.set(response.unread_count);
        this._inboxLoading.set(false);
      },
      error: (err) => {
        console.error('[ApiNotificationService] loadInbox error', err);
        this._inboxLoading.set(false);
      },
    });
  }

  list(filters: NotificationFilters = {}): Observable<NotificationListResponse> {
    let params = new HttpParams();
    if (filters.is_read !== undefined) params = params.set('is_read', filters.is_read.toString());
    if (filters.category) params = params.set('category', filters.category);
    if (filters.priority) params = params.set('priority', filters.priority);
    if (filters.page) params = params.set('page', filters.page.toString());
    if (filters.page_size) params = params.set('page_size', filters.page_size.toString());

    return this.http.get<NotificationListResponse>(this.API_URL, { params });
  }

  getUnreadCount(): Observable<UnreadCountResponse> {
    return this.http.get<UnreadCountResponse>(`${this.API_URL}/unread-count`);
  }

  markAsRead(notificationId: number): Observable<ApiNotification> {
    return this.http.put<ApiNotification>(`${this.API_URL}/${notificationId}/read`, {});
  }

  markAllAsRead(): Observable<BulkActionResponse> {
    return this.http.put<BulkActionResponse>(`${this.API_URL}/read-all`, {});
  }

  deleteNotification(notificationId: number): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/${notificationId}`);
  }

  deleteAllRead(): Observable<BulkActionResponse> {
    return this.http.delete<BulkActionResponse>(`${this.API_URL}/read`);
  }

  /**
   * Marks a notification as read locally (optimistic) and then syncs with backend.
   * Also refreshes the unread count signal.
   */
  markAsReadAndRefresh(notificationId: number): void {
    this._inboxNotifications.update((items) =>
      items.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n)),
    );
    this._unreadCount.update((c) => Math.max(0, c - 1));

    this.markAsRead(notificationId).subscribe({
      error: () => this.loadInbox(),
    });
  }

  markAllAsReadAndRefresh(): void {
    this._inboxNotifications.update((items) => items.map((n) => ({ ...n, is_read: true })));
    this._unreadCount.set(0);

    this.markAllAsRead().subscribe({
      error: () => this.loadInbox(),
    });
  }

  formatRelativeTime(isoDate: string): string {
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} minuto${diffMins !== 1 ? 's' : ''}`;
    if (diffHours < 24) return `Hace ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
    if (diffDays < 7) return `Hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
    });
  }

  getPriorityIcon(priority: string): string {
    switch (priority) {
      case 'high':
        return 'pi pi-exclamation-circle text-red-500';
      case 'medium':
        return 'pi pi-info-circle text-yellow-500';
      default:
        return 'pi pi-circle text-surface-400';
    }
  }

  getCategoryIcon(category: string): string {
    switch (category) {
      case 'client':
        return 'pi pi-user';
      case 'user':
        return 'pi pi-user-plus';
      case 'shipment':
        return 'pi pi-truck';
      default:
        return 'pi pi-bell';
    }
  }
}
